import { useEffect, useState } from "react";
import {
  getGvcConfig, saveGvcConfig,
  getGdpbConfig, saveGdpbConfig,
  getGnffConfig, saveGnffConfig,
  listDicionarios, getDicionario, saveDicionario,
  getPlanilhaConfig, savePlanilhaConfig,
  changePassword,
  getGavConfig, saveGavConfig,
} from "../api/client";

const TABS = ["GVC", "GDPB", "GNFF", "GAV", "Dicionários", "Planilha", "Senha"];
const CHANNELS = ["ml", "sp_ml", "shopee", "amazon", "nvs"];
const CHANNEL_LABEL = { ml: "Mercado Livre (Clássico + Premium)", sp_ml: "SP-ML (2ª conta ML — SKUs EGSP)" };

function SectionTitle({ children }) {
  return <h3 className="font-semibold text-gray-700 mb-4">{children}</h3>;
}

function FieldRow({ label, children, hint }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-gray-100 last:border-b-0">
      <div>
        <p className="text-sm font-medium text-gray-700">{label}</p>
        {hint && <p className="text-xs text-gray-400 mt-0.5">{hint}</p>}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

function Toggle({ checked, onChange }) {
  return (
    <label className="inline-flex items-center cursor-pointer">
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="sr-only peer" />
      <div className="w-10 h-5 bg-gray-300 peer-checked:bg-blue-700 rounded-full transition-colors relative">
        <div className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-5" />
      </div>
    </label>
  );
}

function SaveBar({ onSave, saving, saved, error }) {
  return (
    <div className="flex items-center gap-3 pt-2">
      <button
        onClick={onSave}
        disabled={saving}
        className="bg-blue-800 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold px-6 py-2 rounded-lg text-sm transition-colors"
      >
        {saving ? "Salvando..." : "Salvar"}
      </button>
      {saved && <span className="text-green-600 text-sm">✓ Salvo</span>}
      {error && <span className="text-red-500 text-sm">{error}</span>}
    </div>
  );
}

// ─── GVC ──────────────────────────────────────────────────────────────────────

function GvcSettings() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    getGvcConfig().then(({ data }) => setConfig(data)).finally(() => setLoading(false));
  }, []);

  const setRule = (key, value) =>
    setConfig((prev) => ({ ...prev, rules: { ...prev.rules, [key]: value } }));

  // Mercado Livre é UM canal ("ml" = Clássico + Premium). Configs antigas guardam "mlc"/"mlp"
  // separados — conta como ligado se qualquer um estiver, e ao alternar normaliza pra "ml".
  const ML_LEGACY = ["ml", "mlc", "mlp"];
  const isChannelOn = (ch) =>
    ch === "ml"
      ? ML_LEGACY.some((k) => config?.channels_enabled?.includes(k))
      : config?.channels_enabled?.includes(ch) ?? false;
  const toggleChannel = (ch) =>
    setConfig((prev) => {
      const cur = prev.channels_enabled || [];
      if (ch === "ml") {
        const rest = cur.filter((c) => !ML_LEGACY.includes(c));
        const on = ML_LEGACY.some((k) => cur.includes(k));
        return { ...prev, channels_enabled: on ? rest : [...rest, "ml"] };
      }
      const enabled = cur.includes(ch) ? cur.filter((c) => c !== ch) : [...cur, ch];
      return { ...prev, channels_enabled: enabled };
    });

  const save = async () => {
    setSaving(true);
    setSaved(false);
    setError("");
    try {
      const { data } = await saveGvcConfig(config);
      setConfig(data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-gray-400 text-sm">Carregando...</p>;
  if (!config) return <p className="text-red-500 text-sm">Não foi possível carregar a configuração do GVC.</p>;

  const r = config.rules || {};

  const sched = config.schedule || {};
  const intervalo = Number(sched.interval_minutes) || 0;
  const setSchedule = (key, value) =>
    setConfig((prev) => ({ ...prev, schedule: { ...prev.schedule, [key]: value } }));
  const setIntervalo = (horas, minutos) =>
    setSchedule("interval_minutes", Math.max(1, horas * 60 + minutos));

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow p-6">
        <SectionTitle>Execução automática</SectionTitle>
        <p className="text-xs text-gray-400 mb-2 -mt-2">
          Roda a auditoria completa (<code>--all</code>) sozinha, de tempos em tempos. Nunca roda junto com uma
          execução manual: se você estiver rodando o GVC na mão quando der a hora, o agendador pula essa vez e
          espera a próxima; enquanto a automática estiver rodando, a execução manual fica bloqueada.
        </p>
        <FieldRow label="Ativar execução automática">
          <Toggle checked={sched.enabled} onChange={(v) => setSchedule("enabled", v)} />
        </FieldRow>
        <FieldRow
          label="Rodar a cada"
          hint={intervalo > 0 ? `Equivale a ${intervalo} minutos entre uma execução e outra.` : "Informe ao menos 1 minuto."}
        >
          <div className="flex items-center gap-2">
            <input
              type="number" min="0"
              value={Math.floor(intervalo / 60)}
              onChange={(e) => setIntervalo(parseInt(e.target.value, 10) || 0, intervalo % 60)}
              className="w-20 border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-500">h</span>
            <input
              type="number" min="0" max="59"
              value={intervalo % 60}
              onChange={(e) => setIntervalo(Math.floor(intervalo / 60), parseInt(e.target.value, 10) || 0)}
              className="w-20 border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-500">min</span>
          </div>
        </FieldRow>
      </div>

      <div className="bg-white rounded-xl shadow p-6">
        <SectionTitle>Tolerância de preço</SectionTitle>
        <FieldRow
          label="Margem de tolerância (R$)"
          hint="Diferenças de preço menores ou iguais a esse valor não geram divergência no GVC. Ex: 0,01 ignora diferenças de 1 centavo."
        >
          <input
            type="number" step="0.01" min="0"
            value={r.price_tolerance ?? 0.01}
            onChange={(e) => setRule("price_tolerance", parseFloat(e.target.value) || 0)}
            className="w-28 border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </FieldRow>
      </div>

      <div className="bg-white rounded-xl shadow p-6">
        <SectionTitle>Canais habilitados</SectionTitle>
        <div className="flex gap-4 flex-wrap">
          {CHANNELS.map((ch) => (
            <label key={ch} className="flex items-center gap-1.5 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={isChannelOn(ch)}
                onChange={() => toggleChannel(ch)}
                className="rounded"
              />
              {CHANNEL_LABEL[ch] ?? ch.toUpperCase()}
            </label>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow p-6">
        <SectionTitle>Exigir presença por canal</SectionTitle>
        <p className="text-xs text-gray-400 mb-2 -mt-2">Se ativo, a ausência do SKU nesse canal gera divergência.</p>
        <FieldRow label="Planilha"><Toggle checked={r.require_planilha} onChange={(v) => setRule("require_planilha", v)} /></FieldRow>
        <FieldRow label="Bling"><Toggle checked={r.require_bling} onChange={(v) => setRule("require_bling", v)} /></FieldRow>
        <FieldRow label="Exigir anúncio Mercado Livre Clássico"><Toggle checked={r.require_mlc} onChange={(v) => setRule("require_mlc", v)} /></FieldRow>
        <FieldRow label="Exigir anúncio Mercado Livre Premium"><Toggle checked={r.require_mlp} onChange={(v) => setRule("require_mlp", v)} /></FieldRow>
        <FieldRow
          label="Grupos sem anúncio Premium"
          hint="Grupo de preço da planilha (coluna grupo) que não tem Premium no ML: o GVC não cobra, acusa erro se existir, e o GEG não cria. Separe por vírgula; basta o nome conter o texto (ex.: Protegido pega FK-POP-Protegido)."
        >
          <input
            type="text"
            value={(r.grupos_sem_premium ?? []).join(", ")}
            onChange={(e) => setRule("grupos_sem_premium", e.target.value.split(",").map((g) => g.trim()).filter(Boolean))}
            className="w-72 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </FieldRow>
        <FieldRow label="Shopee"><Toggle checked={r.require_shopee} onChange={(v) => setRule("require_shopee", v)} /></FieldRow>
        <FieldRow label="Amazon"><Toggle checked={r.require_amazon} onChange={(v) => setRule("require_amazon", v)} /></FieldRow>
        <FieldRow label="Nuvemshop (NVS)"><Toggle checked={r.require_nvs} onChange={(v) => setRule("require_nvs", v)} /></FieldRow>
      </div>

      <div className="bg-white rounded-xl shadow p-6">
        <SectionTitle>Outras regras</SectionTitle>
        <FieldRow label="Mínimo de imagens exigidas">
          <input
            type="number" min="0"
            value={r.min_images ?? 3}
            onChange={(e) => setRule("min_images", parseInt(e.target.value, 10) || 0)}
            className="w-20 border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </FieldRow>
        <FieldRow label="Alertar quando estoque zerado em todos os canais">
          <Toggle checked={r.warn_if_stock_zero} onChange={(v) => setRule("warn_if_stock_zero", v)} />
        </FieldRow>
        <FieldRow label="Nome do depósito Amazon FBA no Bling">
          <input
            type="text"
            value={config.amazon_fba_deposit_name ?? ""}
            onChange={(e) => setConfig((prev) => ({ ...prev, amazon_fba_deposit_name: e.target.value }))}
            className="w-40 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </FieldRow>
      </div>

      <SaveBar onSave={save} saving={saving} saved={saved} error={error} />
    </div>
  );
}

// ─── GDPB ─────────────────────────────────────────────────────────────────────

function GdpbSettings() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    getGdpbConfig().then(({ data }) => setConfig(data)).finally(() => setLoading(false));
  }, []);

  const setField = (key, value) => setConfig((prev) => ({ ...prev, [key]: value }));

  const setStoreSyncEnabled = (value) =>
    setConfig((prev) => ({ ...prev, bling_store_sync: { ...prev.bling_store_sync, enabled: value } }));

  const setStoreChannelEnabled = (idx, value) =>
    setConfig((prev) => {
      const channels = [...(prev.bling_store_sync?.channels || [])];
      channels[idx] = { ...channels[idx], enabled: value };
      return { ...prev, bling_store_sync: { ...prev.bling_store_sync, channels } };
    });

  const setNested = (section, key, value) =>
    setConfig((prev) => ({ ...prev, [section]: { ...prev[section], [key]: value } }));

  const save = async () => {
    setSaving(true);
    setSaved(false);
    setError("");
    try {
      const { data } = await saveGdpbConfig(config);
      setConfig(data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-gray-400 text-sm">Carregando...</p>;
  if (!config) return <p className="text-red-500 text-sm">Não foi possível carregar a configuração do GDPB.</p>;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow p-6">
        <SectionTitle>Comportamento geral</SectionTitle>
        <FieldRow label="Dry-run por padrão" hint="Simula sem enviar para o Bling quando nenhuma flag de execução for informada.">
          <Toggle checked={config.dry_run} onChange={(v) => setField("dry_run", v)} />
        </FieldRow>
        <FieldRow label="Usar dimensões do Mercado Livre">
          <Toggle checked={config.use_ml_dimensions} onChange={(v) => setField("use_ml_dimensions", v)} />
        </FieldRow>
        <FieldRow label="Manter imagens baixadas do ML em disco">
          <Toggle checked={config.keep_downloaded_ml_images} onChange={(v) => setField("keep_downloaded_ml_images", v)} />
        </FieldRow>
        <FieldRow label="Buscar vínculos produto/loja do Bling">
          <Toggle checked={config.fetch_product_store_links} onChange={(v) => setField("fetch_product_store_links", v)} />
        </FieldRow>
      </div>

      <div className="bg-white rounded-xl shadow p-6">
        <SectionTitle>Valores padrão de cadastro</SectionTitle>
        <div className="grid grid-cols-2 gap-4">
          {[
            ["default_brand", "Marca padrão"],
            ["default_unit", "Unidade padrão"],
            ["default_product_type", "Tipo de produto padrão"],
            ["default_situation", "Situação padrão"],
            ["default_format", "Formato padrão"],
          ].map(([key, label]) => (
            <div key={key}>
              <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
              <input
                type="text"
                value={config[key] ?? ""}
                onChange={(e) => setField(key, e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow p-6">
        <SectionTitle>Sincronização produto/loja (Bling multiloja)</SectionTitle>
        <FieldRow label="Habilitado">
          <Toggle checked={config.bling_store_sync?.enabled} onChange={setStoreSyncEnabled} />
        </FieldRow>
        {(config.bling_store_sync?.channels || []).map((ch, idx) => (
          <FieldRow key={ch.channel_key || idx} label={ch.name || ch.channel_key}>
            <Toggle checked={ch.enabled} onChange={(v) => setStoreChannelEnabled(idx, v)} />
          </FieldRow>
        ))}
      </div>

      <div className="bg-white rounded-xl shadow p-6">
        <SectionTitle>Outras integrações</SectionTitle>
        <FieldRow label="Criação de anúncio ML Premium" hint="ml_premium_listing.enabled">
          <Toggle
            checked={config.ml_premium_listing?.enabled}
            onChange={(v) => setNested("ml_premium_listing", "enabled", v)}
          />
        </FieldRow>
        <FieldRow label="Sincronização de preço mínimo ML" hint="ml_min_price_sync.enabled">
          <Toggle
            checked={config.ml_min_price_sync?.enabled}
            onChange={(v) => setNested("ml_min_price_sync", "enabled", v)}
          />
        </FieldRow>
        <FieldRow label="Sincronização de imagens externas (OCI)" hint="external_images.enabled">
          <Toggle
            checked={config.external_images?.enabled}
            onChange={(v) => setNested("external_images", "enabled", v)}
          />
        </FieldRow>
      </div>

      <SaveBar onSave={save} saving={saving} saved={saved} error={error} />
    </div>
  );
}

// ─── GNFF ─────────────────────────────────────────────────────────────────────

// Campo de emitente/destinatário da nota (gnff_config.json). Fora do GnffSettings de
// propósito: componente declarado dentro do render é recriado a cada tecla e o campo perde
// o foco. CNPJ, IE, CEP e código do município gravam só dígitos (formato do XML da NF-e).
function CampoNota({ config, setBloco, bloco, campo, label, digitos, dica, largo }) {
  return (
    <div className={largo ? "col-span-2" : ""}>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        type="text"
        value={(config[bloco] || {})[campo] ?? ""}
        onChange={(e) => setBloco(bloco, campo, digitos ? e.target.value.replace(/\D/g, "") : e.target.value)}
        className={`w-full border border-gray-300 rounded-lg px-3 py-2 text-sm ${digitos ? "font-mono" : ""} focus:outline-none focus:ring-2 focus:ring-blue-500`}
      />
      {dica && <p className="mt-1 text-xs text-gray-400">{dica}</p>}
    </div>
  );
}

function GnffSettings() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    getGnffConfig().then(({ data }) => setConfig(data)).finally(() => setLoading(false));
  }, []);

  const setNota = (key, value) =>
    setConfig((prev) => ({ ...prev, nota: { ...prev.nota, [key]: value } }));
  const setBloco = (bloco, key, value) =>
    setConfig((prev) => ({ ...prev, [bloco]: { ...(prev[bloco] || {}), [key]: value } }));

  const save = async () => {
    setSaving(true);
    setSaved(false);
    setError("");
    try {
      const { data } = await saveGnffConfig(config);
      setConfig(data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-gray-400 text-sm">Carregando...</p>;
  if (!config) return <p className="text-red-500 text-sm">Não foi possível carregar a configuração do GNFF.</p>;

  const n = config.nota || {};

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow p-6">
        <SectionTitle>Nota fiscal de entrada (fake)</SectionTitle>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              NCM padrão
            </label>
            <input
              type="text"
              value={n.ncm_padrao ?? ""}
              onChange={(e) => setNota("ncm_padrao", e.target.value.replace(/\D/g, ""))}
              placeholder="ex: 39264000"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="mt-1 text-xs text-gray-400">
              Usado quando o SKU/planilha não tem NCM próprio. Só dígitos (8 dígitos) — pontos são removidos automaticamente.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Unidade</label>
            <input
              type="text"
              value={n.unidade ?? ""}
              onChange={(e) => setNota("unidade", e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Natureza da operação</label>
            <input
              type="text"
              value={n.nat_op ?? ""}
              onChange={(e) => setNota("nat_op", e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">CFOP</label>
            <input
              type="text"
              value={n.cfop ?? ""}
              onChange={(e) => setNota("cfop", e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Série</label>
            <input
              type="text"
              value={n.serie ?? ""}
              onChange={(e) => setNota("serie", e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Próximo número da nota
            </label>
            <input
              type="number"
              value={n.numero_atual ?? 0}
              onChange={(e) => setNota("numero_atual", parseInt(e.target.value, 10) || 0)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="mt-1 text-xs text-gray-400">Incrementado automaticamente a cada nota gerada.</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow p-6">
        <SectionTitle>Emitente (sua empresa)</SectionTitle>
        <div className="grid grid-cols-2 gap-4">
          <CampoNota config={config} setBloco={setBloco} bloco="emitente" campo="xNome" label="Razão social" largo />
          <CampoNota config={config} setBloco={setBloco} bloco="emitente" campo="cnpj" label="CNPJ" digitos dica="Só dígitos — pontos, barra e traço são removidos." />
          <CampoNota config={config} setBloco={setBloco} bloco="emitente" campo="ie" label="Inscrição estadual" digitos />
        </div>
      </div>

      <div className="bg-white rounded-xl shadow p-6">
        <SectionTitle>Destinatário (fornecedor da nota)</SectionTitle>
        <div className="grid grid-cols-2 gap-4">
          <CampoNota config={config} setBloco={setBloco} bloco="destinatario" campo="xNome" label="Razão social" largo />
          <CampoNota config={config} setBloco={setBloco} bloco="destinatario" campo="cnpj" label="CNPJ" digitos />
          <CampoNota config={config} setBloco={setBloco} bloco="destinatario" campo="ie" label="Inscrição estadual" digitos />
          <CampoNota config={config} setBloco={setBloco} bloco="destinatario" campo="xLgr" label="Logradouro" />
          <CampoNota config={config} setBloco={setBloco} bloco="destinatario" campo="nro" label="Número" />
          <CampoNota config={config} setBloco={setBloco} bloco="destinatario" campo="xBairro" label="Bairro" />
          <CampoNota config={config} setBloco={setBloco} bloco="destinatario" campo="cep" label="CEP" digitos dica="Pode digitar com traço (93600-510) — grava só os dígitos." />
          <CampoNota config={config} setBloco={setBloco} bloco="destinatario" campo="xMun" label="Município" />
          <CampoNota config={config} setBloco={setBloco} bloco="destinatario" campo="cMun" label="Código IBGE do município" digitos dica="7 dígitos (ex.: Estância Velha = 4307554)." />
          <CampoNota config={config} setBloco={setBloco} bloco="destinatario" campo="uf" label="UF" />
        </div>
      </div>

      <SaveBar onSave={save} saving={saving} saved={saved} error={error} />
    </div>
  );
}

// ─── Planilha base (Precificacao.xlsx no Drive) ────────────────────────────────

function PlanilhaSettings() {
  const [info, setInfo]       = useState(null);
  const [fileId, setFileId]   = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);
  const [error, setError]     = useState("");

  const load = () =>
    getPlanilhaConfig()
      .then(({ data }) => {
        setInfo(data);
        setFileId(data.file_id || "");
      })
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    setSaved(false);
    setError("");
    try {
      const { data } = await savePlanilhaConfig(fileId.trim());
      setInfo(data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-gray-400 text-sm">Carregando...</p>;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow p-6">
        <SectionTitle>Planilha base (Google Drive)</SectionTitle>
        <p className="text-sm text-gray-500 mb-4">
          Id do arquivo <code className="bg-gray-100 px-1 rounded">Precificacao.xlsx</code> no Drive — fonte de
          dados de todo o sistema (GVC, GDPB, comandos de preço, GNFF). Precisa trocar sempre que o arquivo é
          substituído no Drive (o antigo vai pra lixeira e o novo nasce com id diferente).
        </p>

        <label className="block text-sm font-medium text-gray-700 mb-1">Id do arquivo</label>
        <input
          type="text"
          value={fileId}
          onChange={(e) => setFileId(e.target.value)}
          placeholder="ex: 1jWHgKSy8zPeSs2QgdBXOfQ9Q8aPcHa2S"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <p className="mt-1 text-xs text-gray-400">
          Copie da URL do arquivo no Drive: <code className="bg-gray-100 px-1 rounded">.../d/&lt;ID&gt;/edit</code>.
          Ao salvar, o id é validado no Drive antes de gravar — se estiver na lixeira ou não existir, a troca é recusada.
        </p>

        {info && (
          <div
            className={`mt-4 rounded-lg px-4 py-3 text-sm ${
              info.erro || info.trashed
                ? "bg-red-50 border border-red-200 text-red-700"
                : "bg-green-50 border border-green-200 text-green-700"
            }`}
          >
            {info.erro && <p>Não foi possível validar esse id no Drive: {info.erro}</p>}
            {!info.erro && info.trashed && <p>⚠ Esse arquivo está na lixeira do Drive — troque o id.</p>}
            {!info.erro && !info.trashed && (
              <p>
                ✓ Arquivo ativo: <strong>{info.nome_arquivo}</strong>
                {info.modified_time && <> — modificado em {new Date(info.modified_time).toLocaleString("pt-BR")}</>}
              </p>
            )}
          </div>
        )}
      </div>

      <SaveBar onSave={save} saving={saving} saved={saved} error={error} />
    </div>
  );
}

// ─── GAV — avaliações Amazon (data/config/gav_config.json) ────────────────────

function GavSettings() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    getGavConfig().then(({ data }) => setConfig(data)).finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setConfig((c) => ({ ...c, [k]: v }));

  const save = async () => {
    if (config.dry_run === false && !window.confirm(
      "Desligar o dry-run faz o GAV enviar de verdade a solicitação de avaliação (uma por pedido, sem volta). Confirmar?"
    )) return;
    setSaving(true);
    setSaved(false);
    setError("");
    try {
      const { data } = await saveGavConfig(config);
      setConfig(data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-gray-400 text-sm">Carregando...</p>;
  if (!config) return <p className="text-red-500 text-sm">Não foi possível carregar a configuração do GAV.</p>;

  const num = (k) => (
    <input type="number" value={config[k]} onChange={(e) => set(k, e.target.value === "" ? "" : Number(e.target.value))}
      className="w-24 border border-gray-300 rounded-lg px-2 py-1 text-sm text-right" />
  );

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow p-6">
        <SectionTitle>GAV — solicitação de avaliação na Amazon</SectionTitle>
        <FieldRow label="Ligado" hint="Desligado, o GAV não roda (nem agendado, nem manual)">
          <Toggle checked={config.enabled} onChange={(v) => set("enabled", v)} />
        </FieldRow>
        <FieldRow label="Dry-run (só simula)" hint="Ligado: só registra o que seria enviado. Desligue depois de conferir o dry-run com o Seller Central.">
          <Toggle checked={config.dry_run} onChange={(v) => set("dry_run", v)} />
        </FieldRow>
        <FieldRow label="Horário diário" hint={`No fuso ${config.tz}, qualquer que seja o fuso do servidor`}>
          <input type="time" value={config.horario} onChange={(e) => set("horario", e.target.value)}
            className="border border-gray-300 rounded-lg px-2 py-1 text-sm" />
        </FieldRow>
        <FieldRow label="Dias extras de espera" hint="Somados à liberação (janela + 5 dias após a entrega)">{num("atraso_dias")}</FieldRow>
        <FieldRow label="Máximo de envios por execução">{num("max_envios_por_execucao")}</FieldRow>
        <FieldRow label="Dias da primeira carga" hint="Só vale na primeira sincronização">{num("backfill_dias")}</FieldRow>
      </div>
      <SaveBar onSave={save} saving={saving} saved={saved} error={error} />
    </div>
  );
}

// ─── Senha do login (app/auth.py) ─────────────────────────────────────────────

function SenhaSettings() {
  const [atual, setAtual]     = useState("");
  const [nova, setNova]       = useState("");
  const [repete, setRepete]   = useState("");
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);
  const [error, setError]     = useState("");

  const save = async () => {
    setSaved(false);
    setError("");
    if (nova.length < 10) return setError("A senha nova precisa ter pelo menos 10 caracteres.");
    if (nova !== repete) return setError("A repetição não confere com a senha nova.");
    setSaving(true);
    try {
      await changePassword(atual, nova);
      setAtual(""); setNova(""); setRepete("");
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  const campo = (label, value, set) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        type="password"
        value={value}
        onChange={(e) => set(e.target.value)}
        className="w-full max-w-sm border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow p-6 space-y-4">
        <SectionTitle>Trocar senha do GEG</SectionTitle>
        <p className="text-sm text-gray-500">
          A senha é conferida no servidor e guardada só como hash. Ao trocar, qualquer outra sessão aberta
          (outro computador ou navegador) é encerrada.
        </p>
        {campo("Senha atual", atual, setAtual)}
        {campo("Senha nova (mínimo 10 caracteres)", nova, setNova)}
        {campo("Repita a senha nova", repete, setRepete)}
      </div>
      <SaveBar onSave={save} saving={saving} saved={saved} error={error} />
    </div>
  );
}

// ─── Dicionários (data/dicionarios/*.json) ────────────────────────────────────

const CATEGORIAS_FILE = "categorias_funko.json";
const SUBCATEGORIAS_FILE = "nvs_subcategorias.json";

// nvs_subcategorias.json: {"Categoria pai / Subcategoria": ["palavra", ...]} ↔ linhas do editor
const subcatsFromData = (data) =>
  Object.entries(data).map(([key, palavras]) => {
    const [pai, ...resto] = key.split("/");
    return { pai: pai.trim(), sub: resto.join("/").trim(), palavrasText: (palavras || []).join(", ") };
  });

function DicionariosSettings() {
  const [files, setFiles] = useState([]);
  const [selected, setSelected] = useState("");
  const [categorias, setCategorias] = useState(null); // [{codigo, nome, keywordsText}] — só quando selected === CATEGORIAS_FILE
  const [subcats, setSubcats] = useState(null); // [{pai, sub, palavrasText}] — só quando selected === SUBCATEGORIAS_FILE
  const [raw, setRaw] = useState(""); // JSON bruto — qualquer outro dicionário
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [loadingFile, setLoadingFile] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    listDicionarios()
      .then(({ data }) => {
        setFiles(data);
        if (data.length) setSelected(data.includes(CATEGORIAS_FILE) ? CATEGORIAS_FILE : data[0]);
      })
      .finally(() => setLoadingFiles(false));
  }, []);

  useEffect(() => {
    if (!selected) return;
    setLoadingFile(true);
    setError("");
    getDicionario(selected)
      .then(({ data }) => {
        if (selected === CATEGORIAS_FILE) {
          setCategorias(
            Object.entries(data).map(([codigo, cfg]) => ({
              codigo,
              nome: cfg.nome || "",
              keywordsText: (cfg.keywords || []).join(", "),
              _rest: cfg, // campos fora nome/keywords (ex: nvs_category_id) — preservados no save
            }))
          );
        } else if (selected === SUBCATEGORIAS_FILE) {
          setSubcats(subcatsFromData(data));
        } else {
          setRaw(JSON.stringify(data, null, 2));
        }
      })
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setLoadingFile(false));
  }, [selected]);

  const setCatField = (idx, field, value) =>
    setCategorias((prev) => prev.map((c, i) => (i === idx ? { ...c, [field]: value } : c)));

  const removeCat = (idx) => setCategorias((prev) => prev.filter((_, i) => i !== idx));

  const addCat = () => setCategorias((prev) => [...prev, { codigo: "", nome: "", keywordsText: "", _rest: {} }]);

  const moveCat = (idx, dir) =>
    setCategorias((prev) => {
      const arr = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return prev;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return arr;
    });

  const save = async () => {
    setSaving(true);
    setSaved(false);
    setError("");
    try {
      let payload;
      if (selected === CATEGORIAS_FILE) {
        payload = {};
        for (const cat of categorias) {
          const codigo = cat.codigo.trim().toUpperCase();
          if (!codigo) continue;
          const keywords = cat.keywordsText.split(",").map((k) => k.trim()).filter(Boolean);
          payload[codigo] = { ...cat._rest, nome: cat.nome.trim(), keywords };
        }
      } else if (selected === SUBCATEGORIAS_FILE) {
        payload = {};
        for (const row of subcats) {
          const pai = row.pai.trim();
          const sub = row.sub.trim();
          if (!pai || !sub) continue;
          const palavras = row.palavrasText.split(",").map((k) => k.trim()).filter(Boolean);
          payload[`${pai} / ${sub}`] = palavras;
        }
      } else {
        try {
          payload = JSON.parse(raw);
        } catch {
          throw new Error("JSON inválido — confira vírgulas e chaves antes de salvar.");
        }
      }
      const { data } = await saveDicionario(selected, payload);
      if (selected === CATEGORIAS_FILE) {
        setCategorias(
          Object.entries(data).map(([codigo, cfg]) => ({
            codigo,
            nome: cfg.nome || "",
            keywordsText: (cfg.keywords || []).join(", "),
            _rest: cfg,
          }))
        );
      } else if (selected === SUBCATEGORIAS_FILE) {
        setSubcats(subcatsFromData(data));
      } else {
        setRaw(JSON.stringify(data, null, 2));
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loadingFiles) return <p className="text-gray-400 text-sm">Carregando...</p>;
  if (!files.length) return <p className="text-red-500 text-sm">Nenhum dicionário encontrado em data/dicionarios/.</p>;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow p-6">
        <SectionTitle>Arquivo</SectionTitle>
        <select
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {files.map((f) => (
            <option key={f} value={f}>{f}</option>
          ))}
        </select>
        <p className="mt-2 text-xs text-gray-400">
          Trocar de arquivo sem salvar descarta as edições feitas nele.
        </p>
      </div>

      {loadingFile ? (
        <p className="text-gray-400 text-sm">Carregando dicionário...</p>
      ) : selected === CATEGORIAS_FILE && categorias ? (
        <div className="bg-white rounded-xl shadow p-6">
          <SectionTitle>Categorias Funko (código → nome + palavras-chave)</SectionTitle>
          <p className="text-xs text-gray-400 mb-4 -mt-2">
            Usado pra detectar a categoria/linha do produto a partir da descrição do fornecedor (EPEG) — tanto no
            parser padrão quanto no resolver de SKU Funko. <strong>A ordem importa</strong>: a primeira categoria
            cuja palavra-chave bater no texto vence — use as setas pra mover uma categoria mais específica (ex:
            "Pop House of the Dragon") antes de uma mais genérica (ex: "Pop Games", que tem a palavra-chave "game").
          </p>
          <div className="space-y-3">
            {categorias.map((cat, idx) => (
              <div key={idx} className="border border-gray-200 rounded-lg p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <div className="flex flex-col">
                    <button type="button" onClick={() => moveCat(idx, -1)} disabled={idx === 0} className="text-gray-400 hover:text-gray-700 disabled:opacity-30 text-xs leading-none">▲</button>
                    <button type="button" onClick={() => moveCat(idx, 1)} disabled={idx === categorias.length - 1} className="text-gray-400 hover:text-gray-700 disabled:opacity-30 text-xs leading-none">▼</button>
                  </div>
                  <input
                    type="text"
                    value={cat.codigo}
                    onChange={(e) => setCatField(idx, "codigo", e.target.value)}
                    placeholder="ex: PDS"
                    style={{ textTransform: "uppercase" }}
                    className="w-24 border border-gray-300 rounded-lg px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    type="text"
                    value={cat.nome}
                    onChange={(e) => setCatField(idx, "nome", e.target.value)}
                    placeholder="ex: Pop Disney"
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button type="button" onClick={() => removeCat(idx)} className="text-red-500 hover:underline text-sm shrink-0">
                    remover
                  </button>
                </div>
                <input
                  type="text"
                  value={cat.keywordsText}
                  onChange={(e) => setCatField(idx, "keywordsText", e.target.value)}
                  placeholder="palavras-chave separadas por vírgula (ex: disney, mickey, pixar)"
                  className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            ))}
          </div>
          <button type="button" onClick={addCat} className="mt-3 text-sm text-blue-700 hover:underline">
            + Adicionar categoria
          </button>
        </div>
      ) : selected === SUBCATEGORIAS_FILE && subcats ? (
        <div className="bg-white rounded-xl shadow p-6">
          <SectionTitle>Subcategorias da Nuvemshop — personagens</SectionTitle>
          <div className="text-xs text-gray-500 mb-4 -mt-2 space-y-1">
            <p>
              Usado pela ação <strong>Subcategoria NVS</strong>. Toda subcategoria que existe na Nuvemshop já
              funciona sozinha quando o <strong>nome dela aparece no título</strong> do produto (ex: título com
              "Jujutsu Kaisen" → subcategoria Jujutsu Kaisen) — não precisa cadastrar nada aqui pra isso.
            </p>
            <p>
              Aqui entram só os casos em que o título cita o <strong>personagem</strong> mas não a franquia (ex:
              "Dom Toretto" → Velozes e Furiosos). Categoria pai e subcategoria precisam estar escritas igual
              à Nuvemshop. Palavra curta ou comum pode pegar produto errado — prefira o nome completo
              ("brian o'conner", não "brian").
            </p>
          </div>
          <div className="space-y-2">
            {subcats.map((row, idx) => (
              <div key={idx} className="flex flex-wrap items-center gap-2 border border-gray-200 rounded-lg p-2">
                <input
                  type="text"
                  value={row.pai}
                  onChange={(e) => setSubcats((prev) => prev.map((r, i) => (i === idx ? { ...r, pai: e.target.value } : r)))}
                  placeholder="Categoria pai (ex: Animation)"
                  className="w-40 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  value={row.sub}
                  onChange={(e) => setSubcats((prev) => prev.map((r, i) => (i === idx ? { ...r, sub: e.target.value } : r)))}
                  placeholder="Subcategoria (ex: Dragon Ball)"
                  className="w-48 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  value={row.palavrasText}
                  onChange={(e) => setSubcats((prev) => prev.map((r, i) => (i === idx ? { ...r, palavrasText: e.target.value } : r)))}
                  placeholder="personagens separados por vírgula (ex: goku, vegeta)"
                  className="flex-1 min-w-[12rem] border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={() => setSubcats((prev) => prev.filter((_, i) => i !== idx))}
                  className="text-red-500 hover:underline text-sm shrink-0"
                >
                  remover
                </button>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setSubcats((prev) => [...prev, { pai: "", sub: "", palavrasText: "" }])}
            className="mt-3 text-sm text-blue-700 hover:underline"
          >
            + Adicionar subcategoria
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow p-6">
          <SectionTitle>{selected} (edição em JSON)</SectionTitle>
          <p className="text-xs text-gray-400 mb-2 -mt-2">
            Sem editor estruturado pra este arquivo ainda — edite o JSON diretamente. Cuidado com vírgulas e chaves.
          </p>
          <textarea
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            rows={20}
            spellCheck={false}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      )}

      <SaveBar onSave={save} saving={saving} saved={saved} error={error} />
    </div>
  );
}

// ─── Página ───────────────────────────────────────────────────────────────────

export default function Configuracoes() {
  const [tab, setTab] = useState(TABS[0]);

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-800">Configurações</h2>

      <div className="flex gap-2 border-b border-gray-200">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t ? "border-blue-700 text-blue-800" : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "GVC" && <GvcSettings />}
      {tab === "GDPB" && <GdpbSettings />}
      {tab === "GNFF" && <GnffSettings />}
      {tab === "GAV" && <GavSettings />}
      {tab === "Dicionários" && <DicionariosSettings />}
      {tab === "Planilha" && <PlanilhaSettings />}
      {tab === "Senha" && <SenhaSettings />}
    </div>
  );
}
